import { createBrowserClient } from '@supabase/ssr';

/**
 * Browser-side Supabase client.
 *
 * Reads the Supabase URL and anon key from:
 * 1. `window.__SUPABASE_CONFIG__` (injected by the server in the root layout)
 * 2. `process.env.NEXT_PUBLIC_SUPABASE_URL` / `SUPABASE_URL` (standard Next.js public env vars)
 *
 * If none are available, throws a clear error so the UI can surface it
 * instead of silently failing.
 */
export function getSupabaseBrowserClient() {
  const { url, key } = resolveBrowserConfig();
  return createBrowserClient(url, key);
}

function resolveBrowserConfig(): { url: string; key: string } {
  // 1. Server-injected config (works in all environments)
  if (typeof window !== 'undefined') {
    const injected = (window as unknown as { __SUPABASE_CONFIG__?: { url: string; key: string } })
      .__SUPABASE_CONFIG__;
    if (injected?.url && injected?.key) {
      return { url: injected.url, key: injected.key };
    }
  }

  // 2. Standard NEXT_PUBLIC_ env vars (available if set in .env at build time)
  const envUrl = process.env['NEXT_PUBLIC_SUPABASE_URL'] || process.env['SUPABASE_URL'];
  const envKey = process.env['NEXT_PUBLIC_SUPABASE_ANON_KEY'] || process.env['SUPABASE_ANON_KEY'];
  if (envUrl && envKey) {
    return { url: envUrl, key: envKey };
  }

  throw new Error(
    'Supabase is not configured for the browser. Ensure NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY are set in your environment.'
  );
}
